import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    error: {
        /** Error when more than two modifier keys are used */
        maxModifiers: string;
        /** Error for reserved shortcuts */
        reservedShortcut: string;
    };
    placeholder: {
        /** Placeholder while recording a shortcut */
        recording: string;
        /** Placeholder when no shortcut is set */
        default: string;
    };
    ariaLabel: {
        /** Accessible name for the capture field while not recording — it has no
         *  visible label, and the keycaps/recording placeholder don't name it. */
        field: string;
    };
    tooltip: {
        /** Tooltip label to reset shortcut */
        reset: string;
        /** Tooltip shown when reverting to default would conflict with another
         *  shortcut that has taken these keys. `conflictLabel` is the name of
         *  the command currently bound to those keys; rendered in bold. */
        resetBlocked: ({ conflictLabel }: {
            conflictLabel: string;
        }) => string | React.ReactNode;
        /** Tooltip label to edit shortcut — clears the current value and focuses the input */
        edit: string;
        /** Tooltip label to clear shortcut binding */
        clear: string;
    };
    button: {
        /** Label for reset shortcut button */
        reset: string;
        /** Label for edit shortcut button */
        edit: string;
        /** Label for clear shortcut button */
        clear: string;
    };
    conflict: {
        /** Conflict tooltip — `conflictLabel` is the command currently bound
         *  to the same keys; rendered in bold. */
        inUseBy: ({ conflictLabel }: {
            conflictLabel: string;
        }) => string | React.ReactNode;
    };
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            error: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Error when more than two modifier keys are used */
                        maxModifiers: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.error.maxModifiers?c=2d082onu8zrbq", defaultMessage: "Maximum of 2 modifier keys allowed" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error for reserved shortcuts */
                        reservedShortcut: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.error.reservedShortcut?c=1vvqwonqgr4sk", defaultMessage: "This shortcut is reserved by the system or browser" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            placeholder: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Placeholder while recording a shortcut */
                        recording: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.placeholder.recording?c=r1pdy64aq3pz", defaultMessage: "Enter shortcut" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Placeholder when no shortcut is set */
                        default: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.placeholder.default?c=7bioz93qext9", defaultMessage: "Click to enter shortcut" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            ariaLabel: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Accessible name for the capture field while not recording — it has no
                         *  visible label, and the keycaps/recording placeholder don't name it. */
                        field: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.ariaLabel.field?c=1rc0zvzms6era", defaultMessage: "Edit keyboard shortcut" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            tooltip: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Tooltip label to reset shortcut */
                        reset: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.tooltip.reset?c=1j301zmnau01i", defaultMessage: "Reset shortcut to default" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Tooltip shown when reverting to default would conflict with another
                         *  shortcut that has taken these keys. `conflictLabel` is the name of
                         *  the command currently bound to those keys; rendered in bold. */
                        resetBlocked: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ conflictLabel }: {
                                    conflictLabel: string;
                                }) => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.tooltip.resetBlocked?c=3cz7knjzhqw77", defaultMessage: "<jsx>Can''t reset because  <strong>{conflictLabel}</strong>  is using the default keys for this shortcut. Clear or reset that shortcut first.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), strong: (__children__: React.ReactNode) => <strong>{React.Children.toArray(__children__)}</strong>, conflictLabel: conflictLabel }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Tooltip label to edit shortcut — clears the current value and focuses the input */
                        edit: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.tooltip.edit?c=3rygvsayffad7", defaultMessage: "Edit" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Tooltip label to clear shortcut binding */
                        clear: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.tooltip.clear?c=x1cns60yai63", defaultMessage: "Clear shortcut" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            button: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Label for reset shortcut button */
                        reset: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.button.reset?c=15tfx5mx8vyw0", defaultMessage: "Reset" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Label for edit shortcut button */
                        edit: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.button.edit?c=3rygvsayffad7", defaultMessage: "Edit" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Label for clear shortcut button */
                        clear: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.button.clear?c=2k9jcge1dm84e", defaultMessage: "Clear" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            conflict: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Conflict tooltip — `conflictLabel` is the command currently bound
                         *  to the same keys; rendered in bold. */
                        inUseBy: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ conflictLabel }: {
                                    conflictLabel: string;
                                }) => __intl__.intl().formatMessage({ id: "ShortcutCaptureInput.conflict.inUseBy?c=ztcw0htwlqy2", defaultMessage: "<jsx>Shortcut is in use by  <strong>{conflictLabel}</strong>,  clear or change that shortcut first.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), strong: (__children__: React.ReactNode) => <strong>{React.Children.toArray(__children__)}</strong>, conflictLabel: conflictLabel }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
